/**
 * Settings > Integrations > Microsoft 365 (approved mockup v9, Bill 2026-10-27). Every MSP brings
 * its own app, ITAssurance included: step 1 shows the exact values to enter in Entra, step 2 takes
 * the app's IDs, the secret and its expiry, and Test and save signs in with them before saving.
 * One company at a time (option A, approved 2026-10-29): company chips, then that company's app
 * (status, Setup guide, Test, Replace secret, More, Sync now), then its client tenants, what needs
 * attention and history. Client tenants are connected from each client's Technology > Microsoft 365 tab.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type M365AppInfo, type M365AppTenant } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { fmt, fmtDT, parseTs } from "../../lib/format";
import { Loading, Tile } from "../../components/bits";
import { timeOf, useSort } from "../../components/Sort";
import { CompanyChips, GuideButton, GuidePicker, MoreMenu, useGuides } from "./SetupGuide";

const plusMonths = (n: number) => { const d = new Date(); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 10); };
const ago = (s: string | null) => {
  const d = parseTs(s); if (!d) return "never";
  const h = (Date.now() - d.getTime()) / 3600e3;
  return h < 1 ? "within the hour" : h < 24 ? `${Math.round(h)} hour${Math.round(h) === 1 ? "" : "s"} ago` : `${Math.round(h / 24)} days ago`;
};
const ACTION: Record<string, string> = {
  "clienthub.m365.app_save": "App details saved", "clienthub.m365.app_remove": "App disconnected", "clienthub.m365.connect": "Tenant connection started",
  "clienthub.m365.approved": "Client's admin approved", "clienthub.m365.remove": "Tenant removed",
};

function copy(text: string, toast: (m: string) => void) {
  void navigator.clipboard?.writeText(text).then(() => toast("Copied."), () => toast("Couldn't copy; select it and copy by hand."));
}

export function M365Integration({ codes }: { codes: string[] }) {
  const { toast, ws } = useStore();
  const [info, setInfo] = useState<Record<string, M365AppInfo> | null>(null);
  const [coPick, setCoPick] = useState<string>(codes.includes(ws) ? ws : "");
  const [picking, setPicking] = useState(false);
  const [form, setForm] = useState<{ code: string; mode: "setup" | "secret" } | null>(null);
  const [f, setF] = useState({ app_id: "", directory_id: "", secret: "", expires: plusMonths(24) });
  const [busy, setBusy] = useState<string | null>(null);
  const [sub, setSub] = useState<"tenants" | "attention" | "history">("tenants");
  const [progress, setProgress] = useState("");
  const load = async () => {
    const all = await Promise.all(codes.map((code) => api.m365App(code).then((r) => [code, r] as const)));
    setInfo(Object.fromEntries(all));
  };
  useEffect(() => { load().catch((e: Error) => toast(e.message)); }, []);
  // The company in view: picked, else the first one set up, else the first.
  const co = codes.includes(coPick) ? coPick : (codes.find((c) => info?.[c]?.app) ?? codes[0] ?? "");
  const { g, save: saveGuide } = useGuides(co);
  const tenants = useMemo(() => (info?.[co]?.tenants ?? []).map((t) => ({ ...t, company: co })), [info, co]);
  const attention = tenants.filter((t) => t.status !== "connected" || t.last_error);
  const { sorted, th } = useSort("set.m365.tenants", sub === "attention" ? attention : tenants, {
    client: (t) => t.client_name.toLowerCase(), cloud: (t) => t.cloud, status: (t) => t.status, lic: (t) => (t.bought ? t.assigned : null), read: (t) => timeOf(t.last_sync_at),
  }, { key: "client", dir: "asc" });
  if (!info) return <Loading />;
  const set = Object.values(info).filter((i) => i.app);
  const many = codes.length > 1;

  const run = async (k: string, fn: () => Promise<unknown>, msg?: string) => {
    setBusy(k);
    try { const r = (await fn()) as { ok?: boolean; error?: string } | undefined; if (r && r.ok === false) toast(r.error ?? "It didn't work."); else if (msg) toast(msg); await load(); return true; }
    catch (e) { toast((e as Error).message); return false; } finally { setBusy(null); }
  };
  const open = (code: string, mode: "setup" | "secret") => {
    const a = info[code]?.app;
    setF({ app_id: a?.app_id ?? "", directory_id: a?.directory_id ?? "", secret: "", expires: plusMonths(24) });
    setForm({ code, mode });
  };
  const save = async () => {
    if (!form) return;
    const ok = await run("save", () => api.m365AppSave({ company: form.code, app_id: f.app_id.trim(), directory_id: f.directory_id.trim(), client_secret: f.secret.trim() || undefined, secret_expires_on: f.expires }),
      form.mode === "secret" ? "New secret saved. Clients don't need to approve again." : "Connected. Now connect each client's tenant from its Technology > Microsoft 365 tab.");
    if (ok) setForm(null);
  };
  const syncAll = async () => {
    setBusy("sync");
    let synced = 0, failed = 0;
    try {
      for (const i of set.filter((x) => x.company === co)) {
        let since: string | undefined;
        for (let round = 0; round < 30; round++) {
          const r = await api.m365AppSync(i.company, since);
          synced += r.synced; failed += r.failed; since = r.since;
          setProgress(`${synced + failed} done${r.remaining ? `, ${r.remaining} to go` : ""}`);
          if (!r.remaining) break;
        }
      }
      toast(failed ? `Read ${synced} tenant${synced === 1 ? "" : "s"}; ${failed} had a problem (see Needs attention).` : `Read ${synced} tenant${synced === 1 ? "" : "s"}.`);
      await load();
    } catch (e) { toast((e as Error).message); } finally { setBusy(null); setProgress(""); }
  };
  const last = tenants.map((t) => t.last_sync_at).filter(Boolean).sort().reverse()[0] ?? null;

  const stepOne = (i: M365AppInfo) => {
    const rows: [string, string, string, boolean][] = [
      ["Name", i.suggested_name, "Your clients' admins see this name when they approve, so use your company's name.", true],
      ["Supported account types", "Multiple Entra ID tenants, then Allow all tenants", "Lets each client's tenant approve it.", false],
      ["Redirect URI (Web)", i.redirect_uri, "", true],
      ["API permissions", `Microsoft Graph, Application: ${i.permissions.join(", ")}`, "All read only: licenses, users, Secure Score, MFA methods and sign-in policies. Nothing that sends mail, changes anything or reads mailboxes or files.", false],
      ["Client secret", "Certificates & secrets, New client secret, 24 months", "Copy its Value right away; Microsoft shows it only once.", false],
    ];
    return (
      <div className="su-step"><span className="su-n">1</span><div className="grow">
        <h4>Register an app in {coName(i.company)}'s Microsoft tenant</h4>
        <div className="cap" style={{ marginBottom: 10 }}>In <b>entra.microsoft.com</b>: Entra ID, App registrations, New registration. Enter exactly these values. Takes about 10 minutes; a Global or Application Administrator does it.</div>
        <div className="tbl-wrap"><table className="tbl cr-t"><tbody>
          {rows.map(([l, v, h, c]) => (
            <tr key={l}><td className="cr-l">{l}</td><td><span className={`cr-v ${c ? "mono" : ""}`}>{v}</span>{h && <div className="cap">{h}</div>}</td>
              <td className="r">{c && <button className="btn btn-ghost btn-sm" onClick={() => copy(v, toast)}>Copy</button>}</td></tr>))}
        </tbody></table></div>
      </div></div>
    );
  };
  const formFor = (code: string) => form?.code === code && (
    form.mode === "setup" ? (
      <div className="su-wrap">
        {stepOne(info[code]!)}
        <div className="su-step"><span className="su-n">2</span><div className="grow">
          <h4>Paste the app's details</h4>
          <div className="form-row" style={{ marginTop: 8 }}>
            <label className="grow"><span className="lbl">Application (client) ID</span><input className="inp mono" value={f.app_id} onChange={(e) => setF({ ...f, app_id: e.target.value })} autoComplete="off" /></label>
            <label className="grow"><span className="lbl">Directory (tenant) ID <span className="help-in">{coName(code)}'s own, for the test</span></span><input className="inp mono" value={f.directory_id} onChange={(e) => setF({ ...f, directory_id: e.target.value })} autoComplete="off" /></label>
          </div>
          <div className="form-row">
            <label className="grow"><span className="lbl">Client secret value{info[code]?.app ? <span className="help-in">leave blank to keep the current one</span> : null}</span>
              <input className="inp mono" type="password" autoComplete="off" value={f.secret} onChange={(e) => setF({ ...f, secret: e.target.value })} placeholder="Paste the secret's Value" /></label>
            <label><span className="lbl">Secret expires</span><input className="inp" type="date" value={f.expires} onChange={(e) => setF({ ...f, expires: e.target.value })} /></label>
          </div>
          <div className="help">Stored encrypted. Nobody can read it back, not even admins; you can only replace it. DocVault reminds admins before it expires.</div>
          <div className="row-btns" style={{ marginTop: 12 }}>
            <button className="btn btn-accent" disabled={busy === "save" || !f.app_id.trim() || !f.directory_id.trim() || (!f.secret.trim() && !info[code]?.app) || !f.expires} onClick={() => void save()}>{busy === "save" ? "Testing…" : "Test and save"}</button>
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button></div>
        </div></div>
      </div>
    ) : (
      <div style={{ margin: "0 0 12px 46px" }}>
        <div className="form-row" style={{ alignItems: "flex-end" }}>
          <label className="grow"><span className="lbl">New client secret value</span><input className="inp mono" type="password" autoComplete="off" value={f.secret} onChange={(e) => setF({ ...f, secret: e.target.value })} placeholder="Paste the new secret's Value" /></label>
          <label><span className="lbl">Expires</span><input className="inp" type="date" value={f.expires} onChange={(e) => setF({ ...f, expires: e.target.value })} /></label>
          <button className="btn btn-accent" disabled={busy === "save" || f.secret.trim().length < 10 || !f.expires} onClick={() => void save()}>{busy === "save" ? "Testing…" : "Test and save"}</button>
          <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
        </div>
        <div className="help">The old secret keeps working until the new one is saved, so there's no gap. Delete the old one in Entra afterward.</div>
      </div>
    )
  );
  const expiring = set.filter((i) => i.app!.secret_days !== null && i.app!.secret_days! <= 60);
  const i = info[co]!, a = i.app;
  const n = i.tenants.filter((t) => t.status !== "pending").length;
  const bought = i.tenants.reduce((x, t) => x + t.bought, 0), assigned = i.tenants.reduce((x, t) => x + t.assigned, 0);
  const statusOf = (c: string) => {
    const x = info[c]?.app;
    if (!x) return { pill: <span className="pill draft sm">Not set up</span>, word: "Not set up" };
    const k = info[c]!.tenants.filter((t) => t.status !== "pending").length;
    const tn = `${k} tenant${k === 1 ? "" : "s"}`;
    if (x.secret_days !== null && x.secret_days < 0) return { pill: <span className="pill bad sm">Secret expired</span>, word: "Secret expired" };
    if (x.status === "error") return { pill: <span className="pill bad sm">Problem</span>, word: "Problem" };
    if (x.secret_days !== null && x.secret_days <= 60) return { pill: <span className="pill warn sm">Secret expires in {x.secret_days} day{x.secret_days === 1 ? "" : "s"}</span>, word: `Secret expires in ${x.secret_days} days` };
    return { pill: <span className="pill pub sm">Connected</span>, word: `Connected · ${tn}` };
  };
  const cur = g?.guides.m365 ?? null;
  const more = [
    ...(a ? [{ label: "Edit app details", onClick: () => open(co, "setup") }] : []),
    ...(g?.can_manage && cur ? [{ label: "Change setup guide", onClick: () => setPicking(true) }] : []),
    ...(a ? [{ label: "Disconnect", danger: true, onClick: () => { if (window.confirm(`Disconnect Microsoft 365 for ${coName(co)}? Licenses stop updating for every client tenant until it's set up again. Connected tenants and what was read stay.`)) void run(`rm:${co}`, () => api.m365AppRemove(co), "Disconnected."); } }] : []),
  ];

  return (
    <>
      {expiring.map((x) => (
        <div key={x.company} className={`note ${x.app!.secret_days! < 0 ? "bad" : "warn"}`} style={{ display: "block", marginBottom: 14 }}>
          <b>{many ? `${coName(x.company)}: ` : ""}The Microsoft 365 app secret {x.app!.secret_days! < 0 ? `expired on ${fmt(x.app!.secret_expires_on!)}` : x.app!.secret_days === 0 ? "expires today" : `expires in ${x.app!.secret_days} day${x.app!.secret_days === 1 ? "" : "s"} (${fmt(x.app!.secret_expires_on!)})`}.</b>{" "}
          {x.app!.secret_days! < 0 ? "Licenses have stopped updating" : "When it does, licenses stop updating"} for {x.tenants.filter((t) => t.status !== "pending").length} client tenants. Make a new secret in Entra (Certificates &amp; secrets, 24 months) and paste it with Replace secret. The clients don't need to approve again.
        </div>
      ))}
      <CompanyChips codes={codes} co={co} pick={(c) => { setCoPick(c); setForm(null); setPicking(false); }} sub={(c) => statusOf(c).word} />
      <div className="ig-box">
        <div className="ig-box-h">
          <div className="grow">
            <div className="ig-box-t"><b>{coName(co)}'s Microsoft app</b> {statusOf(co).pill}</div>
            <div className="cap">{a ? `Re-read every day · ${last ? `last read ${ago(last)}` : "not read yet"}${a.last_error ? ` · ${a.last_error}` : ""}` : `${coName(co)} registers its own app in its own Microsoft tenant, once. Then its clients approve it.`}</div>
          </div>
          <div className="row-btns" style={{ margin: 0 }}>
            <GuideButton g={g} kind="m365" onLink={() => setPicking(true)} />
            {form?.code === co ? <button className="btn btn-ghost btn-sm" onClick={() => setForm(null)}>Cancel</button> : a ? <>
              <button className="btn btn-ghost btn-sm" disabled={busy === `test:${co}`} onClick={() => void run(`test:${co}`, () => api.m365AppTest(co), "The app signs in to Microsoft.")}>Test</button>
              <button className="btn btn-ghost btn-sm" onClick={() => open(co, "secret")}>Replace secret</button>
              <MoreMenu items={more} />
              {n > 0 && <button className="btn btn-accent btn-sm" disabled={busy === "sync"} onClick={() => void syncAll()}>{busy === "sync" ? (progress ? `Reading ${progress}…` : "Reading…") : "Sync now"}</button>}
            </> : <button className="btn btn-accent btn-sm" onClick={() => open(co, "setup")}>Set up</button>}
          </div>
        </div>
        {a ? (
          <div className="ig-facts">
            <div><span>App ID</span><b className="mono">…{a.app_id_end}</b></div>
            <div><span>Secret expires</span><b>{a.secret_expires_on ? fmt(a.secret_expires_on) : "Not set"}</b></div>
            <div><span>Client tenants</span><b>{n} connected{i.tenants.length > n ? `, ${i.tenants.length - n} waiting` : ""}</b></div>
            <div><span>Licenses read</span><b>{bought ? `${assigned} of ${bought} assigned` : "None yet"}</b></div>
          </div>
        ) : form?.code !== co && (
          <ol className="ig-steps"><li>Register the app in Entra (Set up shows the values to copy)</li><li>Paste the app's details and Test and save</li><li>Connect clients from each client's Technology &gt; Microsoft 365 tab</li></ol>
        )}
        {picking && <GuidePicker company={co} label="Microsoft 365" current={cur} onCancel={() => setPicking(false)}
          onPick={(id) => void saveGuide("m365", id).then((ok) => ok && setPicking(false))} onRemove={() => void saveGuide("m365", null).then((ok) => ok && setPicking(false))} />}
        {formFor(co)}
      </div>
      {a && <>
        <div className="seg ig-sub" role="tablist">
          <button className={sub === "tenants" ? "on" : ""} onClick={() => setSub("tenants")}>Client tenants {tenants.length > 0 && <span className="n">{tenants.length}</span>}</button>
          <button className={sub === "attention" ? "on" : ""} onClick={() => setSub("attention")}>Needs attention {attention.length > 0 && <span className="n">{attention.length}</span>}</button>
          <button className={sub === "history" ? "on" : ""} onClick={() => setSub("history")}>History</button>
        </div>
        {sub !== "history" ? (!sorted.length ? <div className="cap">{sub === "attention" ? "Nothing needs attention." : "No client tenants yet. Open a client, then Technology > Microsoft 365 > Connect a tenant."}</div> : (
          <div className="tbl-wrap"><table className="tbl">
            <thead><tr>{th("client", "Client")}{th("cloud", "Cloud")}{th("status", "Status")}{th("lic", "Licenses")}{th("read", "Last read", "hide-md")}<th><span className="sr">Actions</span></th></tr></thead>
            <tbody>{sorted.map((t) => <TenantRow key={t.id} t={t} many={false} busy={busy} run={run} />)}</tbody>
          </table></div>
        )) : (
          <ul className="lst">{i.history.slice(0, 20).map((h, k) => (
            <li key={k}><b>{fmtDT(h.at)}</b> · {ACTION[h.action] ?? h.action}{h.client_name ? `: ${h.client_name}` : ""}<span className="r cap">{h.actor_name ?? ""}</span></li>))}
            {!i.history.length && <li className="cap">Nothing yet.</li>}
          </ul>
        )}
        <div className="cap" style={{ marginTop: 10 }}>Tenants are connected from each client's Technology &gt; Microsoft 365 tab.</div>
      </>}
    </>
  );
}

function TenantRow({ t, many, busy, run }: { t: M365AppTenant & { company: string }; many: boolean; busy: string | null; run: (k: string, fn: () => Promise<unknown>, msg?: string) => Promise<boolean> }) {
  const { toast } = useStore();
  const to = `/clienthub/c/${t.client_id}?tab=ms`;
  const st = t.status === "pending" ? <span className="pill warn sm">Waiting for approval</span>
    : t.status === "error" ? <span className="pill bad sm">{/approv|consent/i.test(t.last_error) ? "Approval removed" : "Problem"}</span> : <span className="pill pub sm">Connected</span>;
  return (
    <tr>
      <td style={{ whiteSpace: "nowrap" }}><Link className="doclink" to={to}><b>{t.client_name}</b></Link>{many && <span className="cap"> · {coName(t.company)}</span>}
        <div className="cap mono">{t.domain || t.display_name || "-"}</div></td>
      <td><span className={`lic ${t.cloud === "gcc" ? "gcc" : ""}`}>{t.cloud === "gcc" ? "GCC" : "Commercial"}</span></td>
      <td>{st}{t.last_error && t.status !== "pending" && <div className="cap" style={{ maxWidth: 260 }}>{t.last_error}</div>}</td>
      <td>{t.bought ? <>{t.assigned} of {t.bought}{t.unused > 0 && <span className="warn-n"> · {t.unused} unused</span>}</> : <span className="cap">-</span>}</td>
      <td className="hide-md">{ago(t.last_sync_at)}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        {t.status === "pending" ? <>
          {t.approve_url && <a className="doclink" onClick={() => copy(t.approve_url!, toast)}>Copy link</a>}{" "}
          <a className="doclink" onClick={() => { if (busy !== t.id) void run(t.id, () => api.m365Check(t.id)); }}>Check</a>
        </> : t.status === "error" ? <Link className="doclink" to={to}>Reconnect</Link> : <Link className="doclink" to={to}>Open</Link>}
      </td>
    </tr>
  );
}
